import { useEffect, useRef } from 'react'

import { soltarElFoco } from './useTeclaAgregar'

/**
 * Enter confirma la acción principal, desde donde esté el foco.
 *
 * Está en su propio archivo y no pegado a una pantalla porque lo usan los tres
 * diálogos del mostrador —la cantidad de un producto, el cobro sin detalle con
 * F3 y la pantalla del local que solo cobra— y el criterio de cuándo el Enter
 * es NUESTRO es lo delicado. Tres copias de eso es una que el día que aparezca
 * un caso nuevo se va a arreglar en un lado nada más.
 *
 * La acción se recibe suelta y no se resuelve acá porque cada pantalla hace
 * algo distinto después: el diálogo lo cierra el POS y la pantalla completa se
 * limpia sola.
 */
export function useEnterConfirma(accion: () => unknown, { activo }: { activo: boolean }) {
  // Va con un ref a la última versión de la acción en lugar de re-suscribir el
  // listener en cada tecla que se toca: la función se recrea en cada render
  // porque depende del monto, del medio y del entregado.
  const ref = useRef(accion)

  useEffect(() => {
    ref.current = accion
  })

  // La tecla que ABRE un diálogo no puede confirmarlo.
  //
  // Parece imposible y pasa: el Enter que agrega el producto marcado en la
  // grilla se despacha al input, React monta el diálogo de cantidad DENTRO de
  // ese mismo despacho, y el listener que se acaba de enganchar en `window`
  // todavía alcanza a recibir el evento antes de que termine de burbujear. El
  // resultado era un diálogo que se abría y se cerraba solo, con el peso de la
  // balanza ya cargado: el cajero nunca lo veía y la mercadería entraba con lo
  // que hubiera arriba del plato.
  //
  // Se arma en el tick siguiente, que es lo único que distingue «la tecla que
  // me abrió» de «una tecla nueva». Es la misma familia que el diálogo del
  // arqueo que se desmontaba solo: un diálogo que aparece y desaparece en el
  // mismo instante no es un atajo, es una decisión que nadie tomó.
  const armado = useRef(false)

  useEffect(() => {
    const id = window.setTimeout(() => {
      armado.current = true
    }, 0)

    return () => {
      window.clearTimeout(id)
      armado.current = false
    }
  }, [])

  // Un clic con el MOUSE deja el foco en el botón que se tocó, y desde ahí el
  // Enter es de ese botón —lo de abajo lo respeta a propósito—. O sea que
  // elegir el medio de pago con el dedo, tipear el importe y apretar Enter no
  // cobraba: volvía a apretar el medio, sin decir nada.
  //
  // Soltarlo en el clic es lo que separa los dos casos que no se distinguen
  // mirando el foco: el que llegó con Tab hasta un botón espera que su Enter lo
  // apriete (esa activación llega como un click con `detail` en cero y no pasa
  // por acá), y el que lo tocó con el mouse ya lo apretó. Es la misma decisión
  // que `soltarElFoco` después de agregar un pago, y acá cubre todos los
  // botones de los cuatro diálogos en lugar de uno.
  useEffect(() => {
    function alTocar(e: MouseEvent) {
      if (e.detail > 0) soltarElFoco()
    }

    // Baja en la CAPTURA por lo mismo que el POS: hay botones que cortan la
    // propagación —el «+» de un renglón lo tiene que hacer, o además
    // seleccionaría la línea— y un listener de subida se los perdería justo a
    // ellos.
    window.addEventListener('click', alTocar, true)
    return () => window.removeEventListener('click', alTocar, true)
  }, [])

  useEffect(() => {
    function alApretar(e: KeyboardEvent) {
      if (e.key !== 'Enter' || e.ctrlKey || e.altKey || e.metaKey) return
      if (!armado.current) return

      // Con una confirmación abierta —o con el cobro ya emitido— el Enter no es
      // de acá: el botón del diálogo tiene el foco y el navegador lo activa
      // solo. Escucharlo también acá dispararía la acción dos veces.
      if (!activo) return

      // Y si el foco está en algo que el Enter ACTIVA por su cuenta, es de eso.
      // Sin esto, llegar con Tab a un medio de pago y apretar Enter cobraba en
      // lugar de elegirlo, y encima el `preventDefault` de abajo se comía el
      // clic que el navegador iba a disparar: el medio nunca quedaba
      // seleccionado. El caso normal —el foco quieto en el campo del importe—
      // no cambia.
      const foco = document.activeElement
      if (foco instanceof HTMLElement) {
        const etiqueta = foco.tagName
        if (etiqueta === 'BUTTON' || etiqueta === 'A' || etiqueta === 'TEXTAREA') return
      }

      e.preventDefault()
      void ref.current()
    }

    window.addEventListener('keydown', alApretar)
    return () => window.removeEventListener('keydown', alApretar)
  }, [activo])
}
